import React, { Fragment, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Transition } from '@headlessui/react'
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import SelectDropdown from '../components/SelectDropdown'
import request from '../utils/request'
import { getCoordinates } from '../utils/geocode'
import { formatDate } from '../utils/formatDate'
import { format, set } from 'date-fns'
import ChatPanel from '../components/ChatPanel'
import { fetchOrderImages } from '../utils/fetchOrderImages';
import ProductCardsDispatch from '../components/ProductCardsDispatch'
import useLockBodyScroll from '../utils/useLockBodyScroll'
import CleaningReportPanel from './CleaningReportPanel'
import { LockboxDetailsLauncher } from './lockbox'
// import ProductCardsDispatch from './ProductCardsDispatch'
/* 


CHECK IF STORE ORDER ID IS A DUPLICATE AND RETURN ERROR IF SO 
SEARCH BY PROPERTY MANAGER


*/
export default function DispatchOrderDrawer({
  order,
  addons = [],
  isOpen,
  refreshOrders,
  closeDrawer,
  drivers,
  properties,
  patchOrder,
  showBroadcast,
}) {
  const [storeArrivalTime, setStoreArrivalTime] = useState(
    order.storeArrivalTime
      ? new Date(order.storeArrivalTime)
      : order.inStockDate
        ? new Date(order.inStockDate)
        : null
  )
  const [storeLocation, setStoreLocation] = useState(order.storeLocation || '')
  const [entryInstructions, setEntryInstructions] = useState(
    order.entryInstructions || ''
  )

  const entryHistory = (order.property.entryInstructionsHistory || [])
    .slice()                                  // copy
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .map(({ previous, updatedAt }) => ({
      text: previous,
      date: new Date(updatedAt),
    }))

  const [dispatchNotes, setDispatchNotes] = useState(order.dispatchNotes || '')
  const [internalNotes, setInternalNotes] = useState(order.internalNotes || '')
  const [storeSpend, setStoreSpend] = useState(order.storeSpend || '')


  const [storeOrderId, setStoreOrderId] = useState(order.storeOrderId || '')
  //Load Images
  const [imageUrls, setImageUrls] = useState([]);
  // const totalCount = order.products?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
  const allOrders = [order, ...addons];
  const totalCount = allOrders
    .flatMap(o => o.products)
    .reduce((sum, p) => sum + (p.quantity || 0), 0);


  const [addedCount, setAddedCount] = useState(null)
  const [totalAddedCount, setTotalAddedCount] = useState(totalCount)
  const [isAdding, setIsAdding] = useState(false)

  const [hosts, setHosts] = useState([])
  const [adminUsers, setAdminUsers] = useState([])
  const { adminInfo } = useSelector((state) => state.login)
  const [selectedDispatcher, setSelectedDispatcher] = useState(
    `${adminInfo.firstName} ${adminInfo.lastName}`
  )

  const [chatOpen, setChatOpen] = useState(false)
  const [openHistory, setOpenHistory] = useState(false)
  const [allowDateChange, setAllowDateChange] = useState(false)
  const [suggestedDrivers, setSuggestedDrivers] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(
    order.propertyID
      ? {
        label: `${order.property.address.line1} ${order.property.address.city}`,
        value: order.propertyID
      }
      : null
  )
  const originalDate = order.inStockDate ? new Date(order.inStockDate) : null;
  const [showHistory, setShowHistory] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const [broadcastOpen, setBroadcastOpen] = useState(false)

  const [needsEntry, setNeedsEntry] = useState(order.needsEntryInstructions || false);
  const [needsTime, setNeedsTime] = useState(order.needsDeliveryTime || false);

  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmError, setConfirmError] = useState(null);

  const [predictedPay, setPredictedPay] = useState(null);
  const [payError, setPayError] = useState(null);
  const [playing, setPlaying] = useState(null);

  const propertyAccessLabel = (
    order?.property?.name ||
    order?.property?.nickname ||
    order?.property?.address?.line1 ||
    (order?.propertyID ? `Property ${order.propertyID}` : undefined)
  );

  //lock screen
  useLockBodyScroll(isOpen || chatOpen)

  // Haversine formula to get miles between two coords
  const haversine = (c1, c2) => {
    const toRad = x => x * Math.PI / 180;
    const R = 3958.8; // Earth radius in miles
    const dLat = toRad(c2.lat - c1.lat);
    const dLon = toRad(c2.lng - c1.lng);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(c1.lat)) * Math.cos(toRad(c2.lat)) *
      Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  useEffect(() => {
    const prop = order.property?.address;
    if (!prop?.lat || !prop?.lng) return;

    const φ = prop.lat * Math.PI / 180;
    const latDelta = 20 / 69;                              // ~0.29°
    const lngDelta = 20 / (Math.cos(φ) * 69);              // wider/narrower by latitude
    const minLat = prop.lat - latDelta, maxLat = prop.lat + latDelta;
    const minLng = prop.lng - lngDelta, maxLng = prop.lng + lngDelta;

    const nearby = drivers
      // 1) quick bounding box test
      .filter(d =>
        d.currentAddress?.lat >= minLat &&
        d.currentAddress?.lat <= maxLat &&
        d.currentAddress?.lng >= minLng &&
        d.currentAddress?.lng <= maxLng
      )
      // 2) accurate Haversine check
      .map(d => {
        const dist = haversine(
          { lat: prop.lat, lng: prop.lng },
          { lat: d.currentAddress.lat, lng: d.currentAddress.lng }

        );
        return { ...d, distance: dist };
      })
      .filter(d => d.distance <= 20);
    nearby.sort((a, b) => a.distance - b.distance);
    setSuggestedDrivers(nearby);

  }, [order.property, drivers]);

  useEffect(() => {
    async function fetchHostsAndAdmins() {
      try {
        const res = await request.get('/hosts');
        if (!res || !res.data) return;

        const allHosts = res.data;
        const admins = allHosts.filter(u => u.isAdmin);

        setHosts(allHosts);
        setAdminUsers(admins);
      } catch (err) {
        console.error('Error fetching hosts/admins:', err);
      }
    }

    fetchHostsAndAdmins();
  }, []);

  useEffect(() => {
    function handleMessage(e) {
      if (e.origin !== window.origin) return;
      // initial OAuth flow:
      if (typeof e.data.addedCount === 'number') {
        setAddedCount(e.data.addedCount);
        setTotalAddedCount(e.data.totalCount);
        // setMessage(`✅ ${e.data.addedCount} of ${e.data.totalCount} items added`);
      }
      if (e.data.cleared) {
        setAddedCount(0);
        setTotalAddedCount(0);
        setMessage('✅ Cart cleared');
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);


  const [dateChanged, setDateChanged] = useState(false)

  //Look to see if user set another date than inStockDate
  useEffect(() => {
    if (!originalDate || !storeArrivalTime) {
      setDateChanged(false)
      return
    }
    // compare just the date-parts
    const origDay = originalDate.toDateString()
    const pickedDay = storeArrivalTime.toDateString()
    setDateChanged(origDay !== pickedDay)
  }, [storeArrivalTime, originalDate])



  useEffect(() => {
    // whenever we open the drawer (or the order changes), fetch the prediction
    if (!isOpen) return

    setPredictedPay(null)
    setPayError(null)

    request
      .get(`/orders/predict/${order._id}`)
      .then(res => {
        setPredictedPay(res.data.predictedPay)
      })
      .catch(err => {
        setPayError("Could not load prediction")
      })
  }, [isOpen, order.uniqueId])




  useEffect(() => {
    if (!isOpen) {
      setImageUrls([])      // clear out when closed
      return
    }

    fetchOrderImages(order.uniqueId)
      .then(urls => {
        setImageUrls(urls)
      })
      .catch(err => {
        console.error('❌ image fetch error', err)
      })
  }, [isOpen, order._id])

  async function handleBroadcast() {
    for (const d of suggestedDrivers) {
      const firstName = d.name.split(' ')[0]
      const msg = `
Hey! ${firstName}, we have an order coming up and wanted to see if you would like to take it. Here are the details!

Property: ${order.property.address.line1}, ${order.property.address.city}
Store Location: ${order.storeLocation}
Driver Pay: $${(order.predictedPay ?? 0).toFixed(2)}
Delivery Date: ${formatDate(order.inStockDate)}
Arrival Time: ${order.storeArrivalTime || '–'}

Please accept as soon as you can as all orders are now first come first serve. Thank you!

– ${adminInfo.firstName}
      `.trim()

      await request.post('/admin/send-push-notification', {
        driverId: d.id,
        title: `New Order Available`,
        body: msg
      })
    }
    setBroadcastOpen(false)
  }

  async function handleResendConfirmation() {
    setConfirmMessage(null);
    setConfirmError(null);
    try {
      // adjust URL to whatever your backend expects
      await request.post(`/orders/${order._id}/resend-invoice`);
      setConfirmMessage('Confirmation email sent!');
    } catch (err) {
      console.error(err);
      setConfirmError('Failed to resend confirmation.');
    }
  }





  function isDispatched(o) {
    if (o.status === 'completed') return false
    const hasDriver = !!o.driverID
    const hasArrival = !!o.storeArrivalTime
    const spend = parseFloat(o.storeSpend)
    const hasValidSpend = !isNaN(spend) && spend > 0
    const hasOrderId = o.storeOrderId?.trim().length > 0
    return hasDriver && hasArrival && hasValidSpend && hasOrderId
  }
  const dispatched = isDispatched(order);
  const hasAddons = addons.length > 0;
  async function handleSubmit() {
    let err = null
    const spendVal = parseFloat(storeSpend)

    if (!storeOrderId.trim()) {
      err = 'Store Order ID is required.'
    } else if (!/^[\d]+\.[\d]{2}$/.test(storeSpend)) {
      err = 'Store Spend must have exactly 2 decimal places (e.g. 12.34).'
    } else if (isNaN(spendVal) || spendVal <= 0) {
      err = 'Store Spend must be a positive number.'
    } else if (!/^[\d]+(\.[\d]{1,2})?$/.test(storeSpend)) {
      err = 'Store Spend must have up to 2 decimal places.'
    } else if (!storeArrivalTime) {
      err = 'Store Arrival Time is required.'
    }

    if (err) {
      setError(err)
      return
    }

    // Only save if dispatchNotes changed
    // Build fullDispatchNotes, but only append guestNote if not already present
    const guestNote = (order.guestNote || '').trim();
    let fullDispatchNotes;
    if (guestNote) {
      const guestNoteBlock = `Guest Notes:\n${guestNote}`;
      // Check if dispatchNotes already contains the guest note block
      if (dispatchNotes.includes(guestNoteBlock)) {
        fullDispatchNotes = dispatchNotes.trim();
      } else {
        // Remove any previous guest note block to avoid stacking
        const cleaned = dispatchNotes.replace(/\n*Guest Notes:[\s\S]*$/m, '').trim();
        fullDispatchNotes = `${cleaned}\n\n${guestNoteBlock}`.trim();
      }
    } else {
      fullDispatchNotes = dispatchNotes.trim();
    }
    const originalFullDispatchNotes = guestNote
      ? `${(order.dispatchNotes || '').trim()}\n\nGuest Notes:\n${guestNote}`
      : (order.dispatchNotes || '').trim();
    if (fullDispatchNotes === originalFullDispatchNotes) {
      setMessage('No changes to save.')
      return
    }

    let storeLat = order.storeLat
    let storeLng = order.storeLng
    if (
      storeLocation.trim() &&
      storeLocation.trim() !== order.storeLocation?.trim()
    ) {
      const coords = await getCoordinates(storeLocation)
      if (coords) {
        storeLat = coords.lat
        storeLng = coords.lng
      }
    }

    const mainPayload = {
      storeArrivalTime,
      storeLocation,
      storeLat,
      storeLng,
      entryInstructions: entryInstructions.trim() || order.property.description,
      dispatchNotes: fullDispatchNotes || 'No Additional Notes',
      internalNotes,
      storeSpend: parseFloat(spendVal.toFixed(2)),
      storeOrderId,
      propertyID: selectedProperty.value,
      dispatcher: selectedDispatcher,
      // mark parent
      isAddOn: false,
      needsEntryInstructions: needsEntry,
      needsDeliveryTime: needsTime
    };

    if (addons.length > 0) {
      const orderGroupItems = [order, ...addons].map(o => ({
        id: o._id,
        uniqueId: o.uniqueId,
      }));
      const parentPayload = {
        ...mainPayload,
        orderGroupItems
      };
      const addonBase = {
        ...mainPayload,
        storeSpend: '0.01',
        isAddOn: true,
        originalOrderId: order._id
      };
      await patchOrder(order._id, parentPayload);
      await Promise.all(
        addons.map(a => patchOrder(a._id, addonBase))
      );
    } else {
      await patchOrder(order._id, mainPayload);
    }
    setMessage('Order updated.')
    refreshOrders({ ...order, ...mainPayload });
    closeDrawer()
  }

  async function handleAddToKrogerCart() {
    setIsAdding(true);
    const summary = [];

    try {
      for (const order of allOrders) {
        const orderId = order._id;
        try {
          const res = await request.post(`/orders/${orderId}/kroger/add`);
          // backend returns { success, addedCount, totalCount, failures }
          summary.push({ orderId, success: true, ...res.data });
        } catch (err) {
          const errData = err.response?.data || {};
          summary.push({
            orderId,
            success: false,
            addedCount: errData.addedCount || 0,
            totalCount: errData.totalCount || order.products.length,
            failures: errData.failures || []
          });
        }
      }

      // sum up items across all orders
      setAddedCount(summary.reduce((sum, r) => sum + (r.addedCount || 0), 0));
      setTotalAddedCount(summary.reduce((sum, r) => sum + (r.totalCount || 0), 0));
    } finally {
      setIsAdding(false);
    }
  }




  function handleAllowDateToggle() {
    setAllowDateChange(prev => {
      const turningOff = prev === true;
      if (turningOff && storeArrivalTime && originalDate) {
        // combine original date + selected time
        const merged = new Date(originalDate);
        merged.setHours(
          storeArrivalTime.getHours(),
          storeArrivalTime.getMinutes(),
          storeArrivalTime.getSeconds(),
          storeArrivalTime.getMilliseconds()
        );
        setStoreArrivalTime(merged);
      }
      return !prev;
    });
  }
  const propertyOptions = properties.map(p => ({
    label: `${p.address?.line1} ${p.address?.city}`,
    value: p._id
  }))


  {/*SETUP FOR ALL NOTES*/ }
  const host = hosts.find(h => h._id === order.property?.hostID) || {}
  const hostNotes = (host.internalHostNotes || '').trim();
  const handleAddHostNotes = () => {
    if (!hostNotes) return;
    setDispatchNotes(prev =>
      prev
        ? `${prev.trim()}\n\nHost Notes:\n${hostNotes}`
        : `Host Notes:\n${hostNotes}`
    );
  };


  const guestNote = (order.guestNote || '').trim();
  const fullDispatchNotes =
    guestNote
      ? `${dispatchNotes.trim()}\n\nGuest Notes:\n${guestNote}`
      : dispatchNotes.trim();


  const supportNote = `Store Location: ${storeLocation || '-'
    }\n\nStore Arrival Time: ${storeArrivalTime ? storeArrivalTime.toLocaleString() : '-'
    }\n\nProperty Address: ${order.property.address?.line1 || '-'
    }, ${order.property.address?.line2 || '-'},${order.property.address?.city || '-'}\n\nDispatch Notes: ${dispatchNotes || '-'
    }\n\nTotal Items: ${totalAddedCount}\nGuest Notes: ${guestNote || '-'
    }\n\nEntry Instructions: ${entryInstructions || order.property.description || '-'
    }`


  return (
    <Transition show={isOpen} as={Fragment}>
      <div className="fixed inset-0 z-50 flex pointer-events-none">
              <div className="ml-auto pointer-events-auto w-screen max-w-[600px] bg-white shadow-xl rounded-xl p-6 flex flex-col">
          <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-semibold">
              Manage Order{' '}
              <span className="font-mono text-base">{order.uniqueId}</span>
            </h2>

            <button
              onClick={() => setChatOpen(true)}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Chat with driver"
            >
              {/* chat icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-5-5H5a2 2 0
                 01-2-2V7a2 2 0 012-2h14a2 2 0
                 012 2v5a2 2 0 01-2 2h-3l-5 5z" />
              </svg>
            </button>
            <button
              onClick={closeDrawer}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <section
              className="
    bg-[#FFFFFF]
    p-8
    rounded-[16px]
    shadow-[0_4px_12px_rgba(0,0,0,0.05)]
    space-y-8
    w-full max-w-4xl mx-auto   /* increased max width */
    font-['Segoe_UI','sans-serif']  /* use Segoe UI */
  "
            >
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h3
                    className="
          text-medium font-bold text-[#374151]
          uppercase tracking-wide
          border-b border-[#E5E7EB] pb-2
        "
                  >
                    Summary
                  </h3>
                </div>
                <button
                  onClick={handleResendConfirmation}
                  className="
        px-1 py-1            /* smaller, more proportionate */
        bg-[#EF4444] hover:bg-[#DC2626]
        text-[#FFFFFF] text-sm font-medium
        rounded-lg transition
      "
                >
                  Send Guest Receipt
                </button>
              </div>

              {/* Success / Error */}
              {confirmMessage && (
                <p className="text-sm text-[#10B981]">{confirmMessage}</p>
              )}
              {confirmError && (
                <p className="text-sm text-[#EF4444]">{confirmError}</p>
              )}

              {/* Main & Add-On Orders */}
              <div className="space-y-6">
                {/* Main Order */}
                <div>
                  <h4 className="text-md font-bold text-[#4B5563]">Main Order</h4>
                  {order.products?.length > 0 ? (
                    <p className="mt-1 text-[#1F2937]">
                      <span className="font-medium">{order.uniqueId}</span> —
                      <span className="font-medium">
                        {order.products.reduce((s, p) => s + (p.quantity || 1), 0)} item
                        {order.products.reduce((s, p) => s + (p.quantity || 1), 0) !== 1 && 's'}
                      </span>
                    </p>
                  ) : (
                    <p className="mt-1 italic text-[#6B7280]">(no items)</p>
                  )}
                </div>

                {/* Add-On Orders */}
                {addons.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#4B5563]">Add-On Orders</h4>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {addons.map(a => {
                        const cnt = a.products?.reduce((s, p) => s + (p.quantity || 1), 0) ?? 0;
                        return (
                          <li key={a._id} className="text-[#1F2937]">
                            <span className="font-medium">{a.uniqueId}</span> —
                            <span className="font-medium">{cnt}</span> item
                            {cnt !== 1 && 's'}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Details Grid */}
              <dl
                className="
      grid grid-cols-1 sm:grid-cols-2
      gap-x-8 gap-y-6
      pt-8 border-t border-[#E5E7EB]
      text-sm
    "
              >
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Total Items:</dt>
                  <dd className="font-mono text-[#10B981] font-semibold">
                    {totalCount}
                  </dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Guest Email:</dt>
                  <dd className="text-[#1F2937]">{order.guestEmail}</dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Guest Phone Number:</dt>
                  <dd className="text-[#1F2937]">{order.guestPhoneNumber}</dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Host Name:</dt>
                  <dd className="text-[#1F2937]">
                    {host.firstName
                      ? `${host.firstName} ${host.lastName}`
                      : 'N/A'}
                  </dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Host Email:</dt>
                  <dd className="text-[#1F2937]">{host.email || '-'}</dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Host Phone:</dt>
                  <dd className="text-[#1F2937]">{host.phoneNumber || '-'}</dd>
                </div>
                <div className="flex">
                  <dt className="w-36 text-[#6B7280] font-medium">Property Address:</dt>
                  <dd className="text-[#1F2937]">
                    {`${order.property.address.line1}, ${order.property.address.line2}, ${order.property.address.city}, ${order.property.address.state} ${order.property.address.zipCode}`}
                  </dd>
                </div>
              </dl>
            </section>


            {/* ───────────── Predicted Driver Pay ───────────── */}
            <div className="mt-6 flex items-center space-x-3">
              <span className="text-[14px] font-semibold text-[#374151]">
                Suggested Driver Pay
              </span>

              <div
                className="
      min-w-[90px]
      px-3 py-1
      rounded-[8px]
      shadow-[0_1px_3px_rgba(0,0,0,0.08)]
      text-[14px] font-mono
      text-center
      bg-[#ECFDF5]
    "
              >
                {predictedPay != null ? (
                  <span className="text-[#059669] font-bold">
                    ${predictedPay.toFixed(2)}
                  </span>
                ) : payError ? (
                  <span className="text-[#DC2626] font-semibold">{payError}</span>
                ) : (
                  <span className="text-[#6B7280]">…</span>
                )}
              </div>
            </div>

<CleaningReportPanel order={order} />
            {/* ───────────── Suggested Drivers ───────────── */}
            <section className="mt-6">
              <button
                type="button"
                onClick={() => setOpenHistory(o => !o)}
                className="
      w-full
      flex items-center justify-between
      bg-[#F3F4F6] hover:bg-[#E5E7EB]
      px-4 py-2
      rounded-[10px]
      text-[13px] font-semibold text-[#374151]
      transition
    "
              >
                Suggested Drivers (≤ 20 mi)
                <ChevronDownIcon
                  className={`h-5 w-5 text-[#6B7280] transition-transform ${openHistory ? 'rotate-180' : ''
                    }`}
                />
              </button>

              <Transition
                show={openHistory}
                enter="transition duration-150 ease-out"
                enterFrom="transform scale-y-0 opacity-0"
                enterTo="transform scale-y-100 opacity-100"
                leave="transition duration-100 ease-in"
                leaveFrom="transform scale-y-100 opacity-100"
                leaveTo="transform scale-y-0 opacity-0"
                className="origin-top"
              >
                <ul className="mt-2 space-y-1 pl-4 text-[13px] text-[#374151] list-disc">
                  {suggestedDrivers.map(d => (
                    <li key={d._id}>
                      {d.name} ({d.distance.toFixed(1)} mi)
                    </li>
                  ))}
                  {suggestedDrivers.length === 0 && (
                    <li className="italic text-[#6B7280]">No drivers within 20 mi</li>
                  )}
                </ul>
              </Transition>
            </section>

            {showBroadcast && suggestedDrivers.length > 0 && (
              <button
                onClick={() => setBroadcastOpen(true)}
                className="px-4 py-2 bg-[#0A84FF] text-white rounded-lg shadow hover:bg-blue-600 transition"
              >
                Ask Suggested Drivers ({suggestedDrivers.length})
              </button>
            )}
            {/* ───────────── Store Arrival Time & Related Controls ───────────── */}
            <section className="space-y-2">
              {/* header + toggle */}
              <div className="flex items-center justify-between">
                <label className="text-[14px] font-semibold text-[#374151]">
                  Store Arrival&nbsp;Time
                </label>

                {/* enable/disable full date editing */}
                <button
                  type="button"
                  onClick={() => handleAllowDateToggle(v => !v)}
                  className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none ${allowDateChange ? 'bg-[#0A84FF]' : 'bg-[#E5E5EA]'
                    }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${allowDateChange ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {/* date / time picker */}
              <DatePicker
                selected={storeArrivalTime}
                onChange={setStoreArrivalTime}
                showTimeSelect
                showTimeSelectOnly={!allowDateChange}
                dateFormat={allowDateChange ? 'Pp' : 'h:mm aa'}
                minDate={new Date(order.inStockDate)}
                placeholderText={formatDate(order.inStockDate)}
                className={`
                  w-full max-w-[340px]
                  bg-[#F9FAFB]
                  border border-[#D1D5DB]
                  rounded-[12px]
                  p-4
                  text-[14px] ${dateChanged ? 'text-[#DC2626] font-bold' : 'text-[#374151]'}
                  focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
                  shadow-[0_1px_3px_rgba(0,0,0,0.08)]
                  transition
                `}
              />

              {/* change alert */}
              {dateChanged && (
                <p className="text-[#DC2626] text-[13px] font-semibold mt-1">
                  ⛔ Delivery date has changed!
                </p>
              )}
            </section>

            {/* ───────────── Store Location ───────────── */}
            <section className="space-y-1 mt-5">
              <label className="text-[14px] font-semibold text-[#374151]">
                Store Location
              </label>

              <input
                type="text"
                value={storeLocation}
                onChange={e => setStoreLocation(e.target.value)}
                placeholder="Enter full address"
                className="
      w-full
      bg-[#F9FAFB]
      border border-[#D1D5DB]
      rounded-[10px]
      px-3 py-2
      text-[14px] text-[#374151]
      placeholder-[#9CA3AF]
      focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
      transition
    "
              />
            </section>

            {/* ───────────── Quick Flags ───────────── */}
            <div className="flex items-center space-x-6 mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={needsEntry}
                  onChange={e => setNeedsEntry(e.target.checked)}
                  className="
        h-4 w-4
        rounded-[4px]
        border border-[#D1D5DB]
        text-[#0A84FF]
        focus:ring-0 focus:outline-none
      "
                />
                <span className="text-[13px] text-[#374151]">
                  Need Entry Instructions
                </span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={needsTime}
                  onChange={e => setNeedsTime(e.target.checked)}
                  className="
        h-4 w-4
        rounded-[4px]
        border border-[#D1D5DB]
        text-[#0A84FF]
        focus:ring-0 focus:outline-none
      "
                />
                <span className="text-[13px] text-[#374151]">
                  Need Delivery Time
                </span>
              </label>
            </div>


            {hostNotes.trim() !== '' && (
              <section className="mb-6 bg-[#FFF9C4] border-l-[6px] border-[#FF6B6B] rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-base font-bold text-[#D32F2F] mb-1">
                      Important Host Notes
                    </h3>
                    <p className="text-sm text-[#000000] whitespace-pre-wrap">
                      {hostNotes}
                    </p>
                  </div>

                  {/* copy‑to‑dispatch button */}
                  <button
                    type="button"
                    onClick={handleAddHostNotes}
                    className="ml-4 px-3 py-1 text-xs font-semibold rounded-md bg-[#FF6B6B] text-white hover:opacity-90"
                  >
                    Add&nbsp;to&nbsp;Dispatch
                  </button>
                </div>
              </section>
            )}

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-[14px] font-semibold text-[#374151]">
                Lockbox &amp; Access Details
              </h3>
              <LockboxDetailsLauncher
                orderId={order?._id}
                propertyLabel={propertyAccessLabel}
                propertyId={order?.propertyID}
              />
            </div>
            <section className="space-y-1">
              <label className="text-[14px] font-semibold text-[#374151]">
                Entry Instructions
              </label>
              <textarea
                rows={8}
                value={entryInstructions}
                onChange={e => setEntryInstructions(e.target.value)}
                placeholder={order.property.description || 'E.g. keypad code is 1234, park in spot #7…'}
                className="
      w-full
      bg-[#F9FAFB]
      border border-[#D1D5DB]
      rounded-[12px]
      p-4
      text-[14px] text-[#374151]
      placeholder-[#9CA3AF]
      shadow-[0_1px_3px_rgba(0,0,0,0.08)]
      focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
      transition
    "
              />
            </section>


            {/* ───────────── Entry Instructions History ───────────── */}
            {entryHistory.length > 0 && (
              <section className="mt-6">
                {/* toggle bar */}
                <button
                  type="button"
                  onClick={() => setShowHistory(h => !h)}
                  className="
        flex items-center justify-between
        w-full
        bg-[#F3F4F6] hover:bg-[#E5E7EB]
        border border-[#D1D5DB]
        px-4 py-2
        rounded-[10px]
        text-[13px] font-semibold text-[#374151]
        transition
      "
                >
                  Entry Instructions History
                  <ChevronDownIcon
                    className={`h-5 w-5 text-[#6B7280] transition-transform ${showHistory ? 'rotate-180' : ''
                      }`}
                  />
                </button>

                {/* collapsible list */}
                <Transition
                  show={showHistory}
                  enter="transition duration-150 ease-out"
                  enterFrom="transform scale-y-0 opacity-0"
                  enterTo="transform scale-y-100 opacity-100"
                  leave="transition duration-100 ease-in"
                  leaveFrom="transform scale-y-100 opacity-100"
                  leaveTo="transform scale-y-0 opacity-0"
                  className="origin-top"
                >
                  <div className="mt-3 max-h-48 overflow-auto space-y-2 pr-1">
                    {entryHistory.map((h, i) => (
                      <div
                        key={i}
                        className="
              bg-[#FFFFFF]
              border border-[#D1D5DB]
              rounded-[10px]
              p-3
              shadow-[0_1px_2px_rgba(0,0,0,0.04)]
            "
                      >
                        <p className="text-[11px] text-[#6B7280] mb-1">
                          {format(h.date, 'PP p')}
                        </p>
                        <p className="text-[13px] text-[#374151] whitespace-pre-wrap">
                          {h.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </Transition>
              </section>
            )}



            <section>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Extra Dispatch Notes
              </label>

              {/* dispatcher‑editable notes */}
              <textarea
                rows={5}
                value={dispatchNotes}
                onChange={e => setDispatchNotes(e.target.value)}
                placeholder="Type any special instructions for the driver…"
                className="
                          w-full
                          bg-[#F9FAFB]
                          border border-[#D1D5DB]
                          rounded-[12px]
                          p-4
                          text-[14px] text-[#374151]
                          placeholder-[#9CA3AF]
                          shadow-[0_1px_3px_rgba(0,0,0,0.08)]
                          focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
                          transition
                        "
              />


              {/* show Guest Notes only when they exist and are non‑empty */}
              {guestNote.trim() && (
                <div className="mt-3 rounded-lg border-l-[5px] border-[#0A84FF] bg-[#F0F8FF] p-3">
                  <p className="text-[13px] font-semibold text-[#0A84FF] mb-1">
                    Guest Notes (auto‑included):
                  </p>
                  <p className="text-[13px] text-[#374151] whitespace-pre-wrap">
                    {guestNote}
                  </p>
                  <p className="mt-2 text-[11px] text-[#6B7280] italic">
                    ➜ These notes will be appended automatically when the order is dispatched.
                    Please don’t re‑type or paste them above.
                  </p>
                </div>
              )}

            </section>


            {/* ───────────── Internal Notes ───────────── */}
            <section className="space-y-1">
              <label className="text-[14px] font-semibold text-[#374151]">
                Internal Notes&nbsp;
                <span className="font-bold text-[#EF4444]">(Bellhopt‑only)</span>
              </label>

              <p className="text-[12px] text-[#6B7280]">
                Visible to Bellhopt staff only. Do&nbsp;<em>not</em> include anything meant
                for drivers or guests.
              </p>

              <textarea
                rows={4}
                value={internalNotes}
                onChange={e => setInternalNotes(e.target.value)}
                placeholder="Include info the staff needs to know ie driver, subs/refunds, issues etc"
                className="
      w-full
      bg-[#FEF2F2]
      border border-[#FCA5A5]
      rounded-[12px]
      p-4
      text-[14px] text-[#374151]
      placeholder-[#FCA5A5]
      shadow-[0_1px_3px_rgba(0,0,0,0.06)]
      focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444] focus:outline-none
      transition
    "
              />
            </section>

            {/* ───────────── Store Spend & Order ID ───────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <div className="space-y-1">
                <label className="text-[14px] font-semibold text-[#374151]">
                  Store Spend
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={storeSpend}
                  onChange={e => setStoreSpend(e.target.value)}
                  className="
        w-full
        bg-[#F9FAFB]
        border border-[#D1D5DB]
        rounded-[10px]
        px-3 py-2
        text-[14px] text-[#374151]
        focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
        transition
      "
                  placeholder="$0.00"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[14px] font-semibold text-[#374151]">
                  Store Order&nbsp;ID
                </label>
                <input
                  type="text"
                  value={storeOrderId}
                  onChange={e => setStoreOrderId(e.target.value)}
                  className="
        w-full
        bg-[#F9FAFB]
        border border-[#D1D5DB]
        rounded-[10px]
        px-3 py-2
        text-[14px] text-[#374151]
        focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
        transition
      "
                  placeholder="e.g. 123‑456‑789"
                  disabled={dispatched}
                />
              </div>
            </div>


            {/* ───────────── Assign Property ───────────── */}
            <section className="space-y-1">
              <label className="text-[14px] font-semibold text-[#374151]">
                Assign Property
              </label>

              <SelectDropdown
                value={selectedProperty}
                options={propertyOptions}
                isMulti={false}
                onChange={setSelectedProperty}
                className="rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]" /* <- tailwind-merge will pass it through */
              />
            </section>

            {/* ───────────── Dispatcher ───────────── */}
            <section className="space-y-1 mt-4">
              <label className="text-[14px] font-semibold text-[#374151]">
                Dispatcher
              </label>

              <select
                value={selectedDispatcher}
                onChange={e => setSelectedDispatcher(e.target.value)}
                className="
      w-full
      bg-[#F9FAFB]
      border border-[#D1D5DB]
      rounded-[10px]
      px-3 py-2
      text-[14px] text-[#374151]
      focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF] focus:outline-none
      transition
    "
              >
                {adminUsers.map(u => {
                  const name = `${u.firstName} ${u.lastName}`;
                  return (
                    <option key={u._id} value={name}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </section>

            {/* ───────────── Support Notes (read‑only) ───────────── */}
            <section className="space-y-1 mt-6">
              <label className="text-[14px] font-semibold text-[#374151]">
                Support Notes
              </label>

              <textarea
                readOnly
                rows={10}
                value={supportNote}
                className="
      w-full
      bg-[#F3F4F6]
      border border-[#D1D5DB]
      rounded-[12px]
      p-4
      text-[12px] font-mono text-[#374151]
      whitespace-pre-wrap
      shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]
    "
              />
            </section>

            {/* ───────────── Products (collapsible) ───────────── */}
            {(order.products?.length > 0 || addons?.length > 0) && (
              <details
                className="
      mb-8
      border border-[#D1D5DB]
      rounded-[12px]
      shadow-[0_1px_3px_rgba(0,0,0,0.06)]
      overflow-hidden
      group
    "
              >
                <summary
                  className="
        flex items-center justify-between
        bg-[#F3F4F6] hover:bg-[#E5E7EB]
        px-4 py-3
        cursor-pointer
        text-[14px] font-semibold text-[#374151]
        transition
      "
                >
                  Product Details
                  <ChevronDownIcon
                    className="
          h-5 w-5 text-[#6B7280] transition-transform
          group-open:rotate-180
        "
                  />
                </summary>

                <div className="px-4 py-6 space-y-6">
                  {[order, ...addons]
                    .sort((a, b) =>
                      a._isAddon === b._isAddon ? 0 : a._isAddon ? 1 : -1
                    )
                    .map(o => (
                      <div key={o.uniqueId} className="space-y-3">
                        <h4 className="font-semibold text-[#1F2937]">
                          {o._isAddon ? `Add‑On ${o.uniqueId}` : o.uniqueId}
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {o.products.map(p => (
                            <ProductCardsDispatch key={p._id} product={p} />
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </details>
            )}

            {/* ───────────── Proof Images ───────────── */}
<section className="w-full">
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    {imageUrls.map((url, idx) => (
      <img
        key={idx}
        src={typeof url === 'string' ? url : url.url}
        alt={`media-${idx}`}
        className="w-full h-full aspect-square object-cover rounded-xl"
      />
    ))}
  </div>
</section>




          </div>


          <footer className="flex-shrink-0 px-6 py-4 bg-gray-50 flex justify-center space-x-3">
            {!dispatched && (
              <button
                onClick={handleAddToKrogerCart}
                disabled={isAdding || (addedCount > 0 && addedCount === totalAddedCount)}
                className="px-4 py-2 min-w-[130px] text-sm font-medium text-white bg-[#10B981] hover:bg-[#059669] rounded-full shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAdding ? 'Adding…' : 'Add to Cart'}
              </button>
            )}

            {/* <button
              onClick={handleClearCart}
              className="px-4 py-2 min-w-[120px] text-sm font-medium text-[#ffffff] bg-[#dc2626] hover:bg-[#b91c1c] rounded-full shadow-sm transition"

            >
              Clear Cart
            </button> */}

            <button
              onClick={handleSubmit}
              className="px-4 py-2 min-w-[120px] text-sm font-medium text-[#ffffff] bg-[#000000] hover:bg-[#1f1f1f] rounded-full shadow-sm transition"
            >
              Save Changes
            </button>
          </footer>

          {addedCount != null && (
            <p className="text-sm  text-center font-medium text-gray-700">
              ✅ {addedCount} of {totalAddedCount} items added
            </p>
          )}
          {(error || message) && (
            <div className="px-6 py-2">
              {error && <p className="text-red-600 text-sm">{error}</p>}
              {message && <p className="text-green-600 text-sm">{message}</p>}
            </div>
          )}

          <button
            onClick={closeDrawer}
            className="py-2 px-4 bg-gray-200 rounded hover:bg-gray-300 text-sm font-medium"
          >
            Cancel
          </button>
        </div>
        <ChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          order={order}
          drivers={drivers}
        />
      </div>
    </Transition>
  )
}
